/* jshint esversion: 6 */
/* globals $logger, $signals, $bridges, $translate, CountdownModel.DISPLAY_REMAINING, CountdownModel.DISPLAY_MODES, CountdownModel.DISPLAY_ELAPSED, CountdownModel.DISPLAY_NONE, CountdownModel.TIMER_DELAY, angular */

/**
 * CountdownModel class manages the countdown functionality for an assessment.
 * It handles the timer logic, persistence, and modal interactions.
 */
class CountdownModel {

    static TIMER_DELAY = 1000;
  
    // countdown display modes
    static DISPLAY_REMAINING = 'remaining';
    static DISPLAY_ELAPSED = 'elapsed';
    static DISPLAY_NONE = 'none';
    static DISPLAY_MODES = [
        CountdownModel.DISPLAY_REMAINING,
        CountdownModel.DISPLAY_ELAPSED,
        CountdownModel.DISPLAY_NONE
    ];

    /**
     * Constructor initializes the CountdownModel instance with various properties.
     * @param {Object} LogBridgeService - Logger service for logging messages.
     * @param {Object} $signals - Signal service for emitting events.
     * @param {Object} $bridges - Bridge service for data persistence.
     * @param {Object} $translate - Translation service for localization.
     * @param {Object} $uibModal - Modal service for opening modals.
     */
    constructor(LogBridgeService, $signals, $bridges, $translate, $uibModal, TelemetryBeaconService) {
        this._logIdentifier = 'CountdownModel';

        this.isOpenModal = {
            quitAssessment: false,
            quitEarly: false,
            eventId: 'leavePackageEarlyByInvigilator'
        };

        this.modalInstance = {
            quitAssessmentModalInstance: null
        };

        this.isRunning = false;
        this.lastTickAt = this.getCurrentDate();
        this.elapsedMillis = 0;
        this.availableMillis = { initial: 0, extra: 0, journal: 0, readingTime: 0 };
        this.countdownId = Math.random();
        this.displayMode = CountdownModel.DISPLAY_REMAINING;
        this.listeners = {};
        this.persistenceEnabled = false;
        this.paused = false;
        this.invigilatorCodeRequiredToResume = false;
        this.mode = undefined;
        this.extraTimeChanged = false;

        this.$logger = LogBridgeService;
        this.$bridges = $bridges;
        this.$translate = $translate;
        this.$uibModal = $uibModal;
        this.$signals = $signals;
        this.TelemetryBeaconService = TelemetryBeaconService;
    }

    /**
     * Handles the error scenario where the assessment is quit due to a time error.
     * Opens a modal to inform the user.
     */
    onErrorQuitAssessment() {
        this.isOpenModal.quitAssessment = true;
        this.isOpenModal.quitEarly = true;
        this.$logger.info(this, 'Assessment ended due to a time error');

        this.modalInstance.quitAssessmentModalInstance = this.$uibModal.open({
            templateUrl: '/views/ib/modules/invigilatorMenu/modal/quitAssessmentTechnicalIssueModal.html',
            windowClass: 'quit-assessment-modal',
            transclude: false,
            backdrop: 'static',
            keyboard: false,
            controller: 'QuitAssessmentModalController',
            resolve: {
                quitEarly: () => this.isOpenModal.quitEarly,
                eventId: () => this.isOpenModal.eventId
            }
        });
    }

    /**
     * Gets the current date and handles potential errors by calling onErrorQuitAssessment.
     * @returns {Date|undefined} - The current date or undefined if an error occurs.
     */
    getCurrentDate() {
        let d = new Date();
        let count = 0;
        let isExitCalled = false;
        while (Object.prototype.toString.call(d) !== '[object Date]' || isNaN(d.getTime())) {
            this.recordTelemetry('clock.nan_retry', 'warning', {
                attempt: count + 1,
                rawValue: String(d),
            });
            d = new Date();
            count++;
            if (count === 3 && !isExitCalled) {
                this.recordTelemetry('clock.nan_detected', 'critical', {
                    retryCount: 3,
                    lastRawValue: String(d),
                });
                this.onErrorQuitAssessment();
                isExitCalled = true;
                return undefined;
            }
        }
        return d;
    }

    /**
     * Starts the countdown measurement.
     */
    startMeasure() {
        this.$logger.info(this, "starting measurement");
        this.isRunning = true;
        this.lastTickAt = this.getCurrentDate();
        this.update();
    }

    /**
     * Toggles the pause state of the countdown.
     * @param {boolean} invigilatorPause - Indicates if the pause is initiated by an invigilator.
     */
    togglePause(invigilatorPause) {
        if (!this.isRunning) {
            this.$logger.info(this, "not pausing because timer is not running");
        }

        this.paused = !this.paused;

        if (this.paused === true && invigilatorPause === true) {
            this.invigilatorCodeRequiredToResume = true;
        } else {
            this.invigilatorCodeRequiredToResume = false;
        }

        this.$logger.info(this, "set pause to " + this.paused + ", invigilatorCodeRequiredToResume: " + this.invigilatorCodeRequiredToResume);
    }

    /**
     * Checks if an invigilator code is required to resume the countdown.
     * @returns {boolean} - True if an invigilator code is required, otherwise false.
     */
    invigilatorRequired() {
        return this.invigilatorCodeRequiredToResume;
    }

    /**
     * Sets whether an invigilator code is required to resume the countdown.
     * @param {boolean} val - The value to set.
     */
    setInvigilatorRequired(val) {
        if (val === true || val === false) {
            this.invigilatorCodeRequiredToResume = val;
        }
    }

    /**
     * Checks if the countdown is currently paused.
     * @returns {boolean} - True if the countdown is paused, otherwise false.
     */
    isPaused() {
        return this.paused;
    }

    /**
     * Updates the countdown state, calculates elapsed time, and handles expiration.
     */
    update() {
        let that = this,
            now = this.getCurrentDate(),
            lastTickAt = this.lastTickAt;

        if (this.isRunning === true) {

            if (this.paused === true) {
                /* Setting last tick and persisting */
                this.lastTickAt = now;
                this.persist();

                /* Setting timeout and emitting signal */
                setTimeout(() => {
                    that.update();
                }, CountdownModel.TIMER_DELAY);
                this.$signals.emit('countdownUpdate');
                return;
            }

            if (Object.prototype.toString.call(lastTickAt) === '[object Date]') {
                this.elapsedMillis += now.getTime() - lastTickAt.getTime();
                // $logger.info("Update ",this.elapsedMillis);
            } else {
                // lastTickAt is not a Date object, set it to the current date
                lastTickAt = this.lastTickAt;
                this.elapsedMillis += now.getTime() - lastTickAt.getTime();
            }

            this.lastTickAt = now;
            this.$signals.emit('countdownUpdate');

            this.persist();

            if (this.isExpired()) {
                this.$logger.info(this, "Time is expired");
                let expSignalName;
                let expMode;
                switch (this.getMode()) {
                    case "readingTime":
                        expSignalName = "countdownExpiredReadingTime";
                        expMode = "readingTime";
                        break;
                    case "journal":
                        expSignalName = "countdownExpiredJournal";
                        expMode = "journal";
                        break;
                    default:
                        expSignalName = "countdownExpired";
                        expMode = "task";
                        break;
                }

                this.recordTelemetry('clock.expired', 'warning', {
                    mode: expMode,
                    totalAvailableMs: this.getTotalAvailableMillis(),
                    elapsedMs: this.elapsedMillis,
                    extraTimeMs: this.getTotalExtraTimeMS(),
                });

                this.$signals.emit(expSignalName);
                this.stopMeasure();
                return;
            }

            setTimeout(() => {
                that.update();
            }, CountdownModel.TIMER_DELAY);
        }
    }

    /**
     * Persists the countdown state if persistence is enabled.
     */
    persist() {
        if (!this.isPersistenceEnabled()) return;
        if (this.getMode() === "readingTime") {
            this.$bridges.data().put('timeElapsed_readingTime', this.elapsedMillis);
            return;
        }

        if (this.getMode() === "task") {
            this.$bridges.data().put('timeElapsed', this.elapsedMillis);
            return;
        }

    }

    /**
     * Persists the display mode if persistence is enabled.
     */
    persistDisplayMode() {
        if (!this.isPersistenceEnabled()) return;
        this.$logger.info(this, 'persisting timer display mode: ' + this.displayMode);
        this.$bridges.data().put('timeDisplayMode', this.displayMode);
    }

    /**
     * Persists the total extra time if persistence is enabled.
     */
    persistTotalExtraTime() {
        if (!this.isPersistenceEnabled()) return;
        this.$logger.info(this, 'persisting total extra time: ' + this.getTotalExtraTimeMS());
        this.$bridges.data().put('extraTimeTotal', this.getTotalExtraTimeMS());
    }

    /**
     * Enables persistence for the countdown if the mode is either 'task' or 'readingTime'.
     */
    enablePersistence() {
        if (this.getMode() !== 'task' && this.getMode() !== 'readingTime') {
            this.persistenceEnabled = false;
            this.$logger.warn("Attempted to enable countdown persistence but we're not in task mode (actually we're in '" + this.getMode() + "' mode). Not enabling.");
            return;
        }
        this.persistenceEnabled = true;
    }

    /**
     * Checks if persistence is enabled for the countdown.
     * @returns {boolean} - True if persistence is enabled, otherwise false.
     */
    isPersistenceEnabled() {
        if (this.getMode() !== 'task' && this.getMode() !== 'readingTime') {
            return false;
        }
        return this.persistenceEnabled;
    }

    /**
     * Checks if the countdown has expired.
     * @returns {boolean} - True if the countdown has expired, otherwise false.
     */
    isExpired() {
        return this.getRemainingMillis() <= 0;
    }

    /**
     * Sets the task as expired and stops the measurement.
     */
    setTaskExpired() {
        this.elapsedMillis = this.getTotalAvailableMillis();
        this.$bridges.data().put('timeElapsed', this.elapsedMillis);
        this.stopMeasure();
    }

    /**
     * Resets the countdown time and mode.
     * @param {number} initTimeMs - Initial time in milliseconds.
     * @param {number} initElapsedTimeMs - Initial elapsed time in milliseconds.
     * @param {string} mode - The mode to set.
     */
    resetTime(initTimeMs, initElapsedTimeMs, mode) {
        this.setMode(mode);
        this.$logger.info(this, "reset time", initTimeMs, initElapsedTimeMs, this.getMode());
        if (this.isRunning === true) this.stopMeasure();
        if (this.getMode() == "readingTime") {
            this.availableMillis.readingTime = typeof (initTimeMs) == 'undefined' ? 0 : initTimeMs;
        } else if (this.getMode() == "journal") {
            this.availableMillis.journal = typeof (initTimeMs) == 'undefined' ? 0 : initTimeMs;
        } else {
            this.availableMillis.initial = typeof (initTimeMs) == 'undefined' ? 0 : initTimeMs;
        }
        //this.availableMillis.extra = 0;
        this.elapsedMillis = angular.isDefined(initElapsedTimeMs) ? initElapsedTimeMs : 0;
    }

    /**
     * Sets the total extra time in milliseconds and optionally persists it.
     * @param {number} totalExtraTimeMs - Total extra time in milliseconds.
     * @param {boolean} persist - Indicates whether to persist the extra time.
     */
    setTotalExtraTimeMS(totalExtraTimeMs, persist) {
        this.extraTimeChanged = true;
        this.$logger.info(this, "setting extra time", totalExtraTimeMs);
        this.availableMillis.extra = totalExtraTimeMs;

        if (persist) {
            this.persistTotalExtraTime();
        }
    }

    /**
     * Checks if the extra time has been changed.
     * @returns {boolean} - True if the extra time has been changed, otherwise false.
     */
    isExtraTimeChanged() {
        return this.extraTimeChanged;
    }

    /**
     * Gets the total extra time in milliseconds.
     * @returns {number} - Total extra time in milliseconds.
     */
    getTotalExtraTimeMS() {
        return this.availableMillis.extra;
    }

    /**
     * Gets the timer delay.
     * @returns {number} - The timer delay.
     */
    getTimerDelay() {
        return CountdownModel.TIMER_DELAY;
    }

    /**
     * Stops the countdown measurement.
     */
    stopMeasure() {
        this.$logger.info(this, "stopping measurement");
        this.isRunning = false;
    }

    /**
     * Sets the display mode for the countdown and optionally persists it.
     * @param {string} mode - The display mode to set.
     * @param {boolean} persist - Indicates whether to persist the display mode.
     */
    setDisplayMode(mode, persist) {
        if (CountdownModel.DISPLAY_MODES.indexOf(mode) > -1) {
            if (typeof (persist) === 'undefined') {
                persist = true;
            }
            this.$logger.info(this, 'set display mode to', mode);
            this.displayMode = mode;

            if (persist) {
                this.persistDisplayMode();
            }
        } else {
            this.$logger.info(this, 'is no valid display mode');
        }
    }

    /**
     * Gets the current display mode.
     * @returns {string} - The current display mode.
     */
    getDisplayMode() {
        return this.displayMode;
    }

    /**
     * Gets the total available milliseconds for the countdown.
     * @returns {number} - Total available milliseconds.
     */
    getTotalAvailableMillis() {
        if (this.getMode() === "readingTime") {
            return this.availableMillis.readingTime;
        } else if (this.getMode() === "journal") {
            return this.availableMillis.journal;
        }
        return this.availableMillis.initial + this.availableMillis.extra;
    }

    /**
     * Gets the initial time in milliseconds.
     * @returns {number} - Initial time in milliseconds.
     */
    getInitialTime() {
        return this.availableMillis.initial;
    }

    /**
     * Sets the initial time in milliseconds.
     * @param {number} val - The value to set.
     */
    setInitialTime(val) {
        this.availableMillis.initial = val;
    }

    /**
     * Gets the remaining milliseconds for the countdown.
     * @returns {number} - Remaining milliseconds.
     */
    getRemainingMillis() {
        return (this.getTotalAvailableMillis() - this.elapsedMillis);
    }

    /**
     * Gets the elapsed milliseconds for the countdown.
     * @returns {number} - Elapsed milliseconds.
     */
    getElapsedMillis() {
        return this.elapsedMillis;
    }

    /**
     * Sets the elapsed milliseconds for the countdown.
     * @param {number} val - The value to set.
     */
    setElapsedMillis(val) {
        this.elapsedMillis = val;
    }

    /**
     * Gets zero milliseconds.
     * @returns {number} - Zero milliseconds.
     */
    getNoMillis() {
        return 0;
    }

    /**
     * Gets the milliseconds based on the specified mode.
     * @param {string} mode - The mode to get milliseconds for.
     * @returns {number} - The milliseconds based on the mode.
     */
    getMillis(mode) {
        let that = this,
            methods = {
                'remaining': 'getRemainingMillis',
                'elapsed': 'getElapsedMillis',
                'none': 'getNoMillis'
            },
            methodKey = CountdownModel.DISPLAY_REMAINING,
            result;

        methodKey = mode !== undefined ? mode : CountdownModel.DISPLAY_REMAINING;

        if (angular.isString(mode) &&
            angular.isFunction(methods[mode.toLowerCase()])) {
            methodKey = mode.toLowerCase();
        }

        result = that[methods[methodKey]]();

        return result;
    }

    /**
     * Gets the seconds based on the current display mode.
     * @returns {number} - The seconds.
     */
    getSeconds() {
        return Math.floor((this.getMillis(this.displayMode) / 1000) % 60);
    }

    /**
     * Gets the minutes based on the current display mode.
     * @returns {number} - The minutes.
     */
    getMinutes() {
        return Math.floor((this.getMillis(this.displayMode) / (1000 * 60)) % 60);
    }

    /**
     * Gets the hours based on the current display mode.
     * @returns {number} - The hours.
     */
    getHours() {
        return Math.floor((this.getMillis(this.displayMode) / (1000 * 60 * 60)) % 24);
    }

    /**
     * Gets the time format based on the current display mode.
     * @returns {string} - The time format.
     */
    getFormat() {
        let format = '{%hh}:{%mm}',
            hours = this.getHours(),
            minutes = this.getMinutes();

        if (this.displayMode == CountdownModel.DISPLAY_NONE) {
            format = ' ';
        } else {
            if (minutes === 0 && hours === 0)
                format = '{%ss} sec';
        }

        return format;
    }

    /**
     * Gets the mode translations.
     * @returns {Object} - An object containing mode translations.
     */
    getModeTranslations() {
        return {
            'REMAINING': CountdownModel.DISPLAY_REMAINING,
            'ELAPSED': CountdownModel.DISPLAY_ELAPSED,
            'NONE': CountdownModel.DISPLAY_NONE
        };
    }

    /**
     * Gets the current mode.
     * @returns {string} - The current mode.
     */
    getMode() {
        return angular.isDefined(this.mode) ? this.mode : 'readingTime';
    }

    /**
     * Sets the mode.
     * @param {string} mode - The mode to set. Can be "journal", "task", or "readingTime". Default is "task".
     */
    setMode(mode) {
        this.mode = angular.isDefined(mode) ? mode : "task"; // this can be set to "journal" or "task", default is "task"
        if (this.getMode() === "readingTime") {
            this.elapsedMillis = this.$bridges.data().get('timeElapsed_readingTime') ? this.$bridges.data().get('timeElapsed_readingTime') : 0;
            return;
        } else if (this.getMode() === "task") {
            this.elapsedMillis = this.$bridges.data().get('timeElapsed') ? this.$bridges.data().get('timeElapsed') : 0;
            return;
        }
        this.elapsedMillis = 0;
    }

    /**
     * Checks if the countdown is running.
     * @returns {boolean} - True if the countdown is running, false otherwise.
     */
    getIsRunning() {
        return this.isRunning;
    }

    /**
     * Sets the total available milliseconds.
     * @param {number} val - The total available milliseconds to set.
     */
    setTotalAvailableMillis(val) {
        if (this.getMode() === "readingTime") {
            this.availableMillis.readingTime = val;
            return;
        } else if (this.getMode() === "journal") {
            this.availableMillis.journal = val;
            return;
        }
        this.availableMillis.initial = val;
    }

    /**
     * For legacy reasons, because in the old app there was a weird singleton concept implemented for this class,
     * I'm putting this here so I don't have to find all usages of TaskCountdownModel.getInstance()
     * @returns just this instance
     */
    getInstance() {
        return this;
    }

    recordTelemetry(eventType, severity, data) {
        // Telemetry must never break core countdown functionality
        try {
            if (this.TelemetryBeaconService && this.TelemetryBeaconService.record) {
                this.TelemetryBeaconService.record(eventType, severity, data);
            }
        } catch (e) {
            // Silently ignore telemetry errors to protect the countdown timer
        }
    }
}

CountdownModel.$inject = ['LogBridgeService', '$signals', '$bridges', '$translate', '$uibModal', 'TelemetryBeaconService'];

export default CountdownModel;
